/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import { CardWithProgress } from '@app/app/page-modules/resource/models';
import {
  ApprovalRequest,
  AssociateCategoryRequest,
  Audience,
  BrowseSettings,
  BrowseSettingsRequest,
  ContentUsage,
  DisplayConfigRequest,
  RegistrationSettings,
} from '../../models';
import { ObservableResult } from '../../store';

/** Injection token for the data service, used by angular to decide whether to inject Mock or API service at runtime. */
export const SHARED_CARD_DATA_SERVICE = new InjectionToken<SharedCardDataService>('[resource] SharedCardDataService');

/** The service handling tag CRUD operations. */
export interface SharedCardDataService {
  /**
   * Fires when resource is opened.
   */
  registerCardOpen(resourceUid: string): ObservableResult<void>;

  getCardDetails(cardUri: string): ObservableResult<CardWithProgress>;

  associateTag(resourceUid: string, tagUid: string): ObservableResult<void>;

  dissociateTag(resourceUid: string, tagUid: string): ObservableResult<void>;

  updateCardUri(resourceUid: string, newUri: string): ObservableResult<void>;

  updateAuthorDisplayConfig(resourceUid: string, request: DisplayConfigRequest): ObservableResult<void>;

  updateBrowseSettings(resourceUid: string, request: BrowseSettingsRequest): ObservableResult<BrowseSettings>;

  publishCard(resourceUid: string): ObservableResult<void>;

  unpublishCard(resourceUid: string): ObservableResult<void>;

  archiveCard(resourceUid: string): ObservableResult<void>;

  updateApprovalMethod(resourceUid: string, request: ApprovalRequest): ObservableResult<void>;

  getReviewsCount(cardId: string): ObservableResult<number>;

  associateReviewer(resourceUid: string, userUid: string): ObservableResult<void>;

  dissociateReviewer(resourceUid: string, userUid: string): ObservableResult<void>;

  associateCategory(cardUid: string, request: AssociateCategoryRequest): ObservableResult<void>;

  dissociateCategory(cardUid: string, categoryTagUid: string): ObservableResult<void>;

  associateTargetGroup(cardId: string, groupUid: string): ObservableResult<void>;

  dissociateTargetGroup(cardId: string, groupUid: string): ObservableResult<void>;

  updateTargetGroupSubscription(cardId: string, groupUid: string, force: boolean): ObservableResult<void>;

  dissociateAllTargetGroups(cardId: string): ObservableResult<void>;

  /** V3 organizations only: programmes whose learners are targeted. */
  associateTargetProgram(cardId: string, programUid: string): ObservableResult<void>;

  updateTargetProgramSubscription(cardId: string, programUid: string, force: boolean): ObservableResult<void>;

  dissociateTargetProgram(cardId: string, programUid: string): ObservableResult<void>;

  associateTargetUser(cardId: string, userUid: string): ObservableResult<void>;

  dissociateTargetUser(cardId: string, userUid: string): ObservableResult<void>;

  /** V3 organizations only. Fails with the API's message when the audience isn't allowed. */
  updateAudience(cardId: string, audience: Audience): ObservableResult<void>;

  /** V3 organizations only, independent cards: learners register first. Fails with the API's message. */
  setRegistration(cardId: string, settings: RegistrationSettings): ObservableResult<void>;

  /** V3 organizations only, independent cards: back to self-enrolment. */
  clearRegistration(cardId: string): ObservableResult<void>;

  /** V3 organizations only: the playlists this card can be opened from. */
  getTargetingUsages(cardId: string): ObservableResult<ContentUsage[]>;
}
